import { getElements } from "@services/storage/elements";
import { getSkinini } from "@services/storage/skinini";
import { rgbCssToString } from "@utils/color";
import { errorMessage, jsonHeaders, readJson } from "@services/api/http";

/**
 * API response for skin save
 */
export interface SaveSkinResponse {
  success: boolean;
  skin_id: string;
  redirect: string;
}

/**
 * Save skin to database
 * @returns Promise with skin_id and redirect URL
 * @throws Error with message on failure
 */
export async function saveSkin(): Promise<SaveSkinResponse> {
  const elements = getElements();
  const skinini = getSkinini();

  // Validate we have something to save
  if (Object.keys(elements).length === 0) {
    throw new Error(
      "No elements selected. Please select at least one element.",
    );
  }

  // Transform elements to expected format (groupId => { element_id })
  const elementsPayload: Record<string, { element_id: string }> = {};
  for (const [groupId, data] of Object.entries(elements)) {
    elementsPayload[groupId] = { element_id: data.element_id };
  }

  // Transform skinini for PHP: convert rgb() to r,g,b and exclude transparent
  const skininiPayload: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(skinini)) {
    if (value === undefined || value === "transparent") {
      continue;
    }
    if (typeof value === "string" && value.startsWith("rgb(")) {
      const rgbString = rgbCssToString(value);
      if (rgbString) {
        skininiPayload[key] = rgbString;
      }
    } else {
      skininiPayload[key] = value;
    }
  }

  const response = await fetch("/project/save", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({
      elements: elementsPayload,
      skinini: skininiPayload,
    }),
  });

  const data = await readJson(response);

  if (!response.ok || data === null) {
    const fallback =
      response.status === 403
        ? "Your session expired. Please reload the page and try again."
        : `Save failed (HTTP ${response.status})`;
    throw new Error(errorMessage(data, fallback));
  }

  return data as SaveSkinResponse;
}
